import { BulkEditRow } from "../../store/bulk-edit-store";
import { ActiveSiteValues, UpdateActiveSitesRequest } from "../../por-api/generated-src";
import { BULK_EDIT_SAVE_BATCH_SIZE } from "./BulkEditConstants";

/**
 * Constructs the save payload for bulk edit.
 * Only includes projects that have at least one changed field.
 * Each project in the payload includes ALL fields (changed + unchanged) in their respective groups,
 * because the backend's toActiveSitesRecord expects all field groups to be present.
 */
export function constructBulkEditPayload(
  rows: BulkEditRow[],
  originalRows: BulkEditRow[]
): { changeBody: ActiveSiteValues[]; changedRowCount: number; changedCellCount: number } {
  const changeBody: ActiveSiteValues[] = [];
  let changedRowCount = 0;
  let changedCellCount = 0;

  for (const row of rows) {
    const originalRow = originalRows.find((r) => r.projectId === row.projectId);
    if (!originalRow) continue;

    let rowHasChanges = false;

    // Check if this row has any changes (regular editable columns)
    for (const [fieldName, fieldData] of Object.entries(row.editableFields)) {
      const originalFieldData = originalRow.editableFields[fieldName];
      if (!originalFieldData) continue;
      if (fieldData.value !== originalFieldData.originalValue) {
        rowHasChanges = true;
        changedCellCount++;
      }
    }

    // Check if this row has any changes (fixed identifier columns made editable:
    // Status, LMSC/SC-SC, Timeline)
    for (const [fieldName, fieldData] of Object.entries(row.identifierEditableFields || {})) {
      const originalFieldData = originalRow.identifierEditableFields?.[fieldName];
      if (!originalFieldData) continue;
      if (fieldData.value !== originalFieldData.originalValue) {
        rowHasChanges = true;
        changedCellCount++;
      }
    }

    if (rowHasChanges) {
      changedRowCount++;

      // Build full field groups with ALL current values (changed + unchanged)
      const buildingAttributes: Record<string, string> = {};
      const productivity: Record<string, string> = {};
      const siteDetails: Record<string, string> = {};
      const retrofitDetails: Record<string, string> = {};

      for (const [fieldName, fieldData] of Object.entries(row.editableFields)) {
        if (fieldData.fieldGroup === "buildingAttributes") {
          buildingAttributes[fieldName] = fieldData.value;
        } else if (fieldData.fieldGroup === "productivity") {
          productivity[fieldName] = fieldData.value;
        } else if (fieldData.fieldGroup === "siteDetails") {
          siteDetails[fieldName] = fieldData.value;
        } else if (fieldData.fieldGroup === "retrofitDetails") {
          retrofitDetails[fieldName] = fieldData.value;
        }
      }

      // Fold in fixed identifier columns made editable (Status, LMSC/SC-SC, Timeline)
      // into their respective source field groups.
      for (const [fieldName, fieldData] of Object.entries(row.identifierEditableFields || {})) {
        if (fieldData.fieldGroup === "buildingAttributes") {
          buildingAttributes[fieldName] = fieldData.value;
        } else if (fieldData.fieldGroup === "productivity") {
          productivity[fieldName] = fieldData.value;
        } else if (fieldData.fieldGroup === "siteDetails") {
          siteDetails[fieldName] = fieldData.value;
        } else if (fieldData.fieldGroup === "retrofitDetails") {
          retrofitDetails[fieldName] = fieldData.value;
        }
      }

      const siteValues: ActiveSiteValues = {
        projectId: row.projectId,
        buildingAttributes: buildingAttributes,
        productivity: productivity,
        siteDetails: siteDetails,
        equipmentDescription: {},
        additionalDetails: {},
        retrofitDetails: retrofitDetails,
      };

      changeBody.push(siteValues);
    }
  }

  return { changeBody, changedRowCount, changedCellCount };
}

/**
 * Splits the changeBody into batches to avoid API Gateway timeout.
 * Each batch is sent as a separate API call, processed sequentially.
 */
export function batchChangeBody(changeBody: ActiveSiteValues[]): ActiveSiteValues[][] {
  const batches: ActiveSiteValues[][] = [];
  for (let i = 0; i < changeBody.length; i += BULK_EDIT_SAVE_BATCH_SIZE) {
    batches.push(changeBody.slice(i, i + BULK_EDIT_SAVE_BATCH_SIZE));
  }
  return batches;
}
